import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import ScaledIframe, { ScaledIframeRef } from "./ScaledIframe";
import { toast } from "../../core/toast";
import { useNavigate } from "react-router-dom";
import {
  Theme,
  ThemeDocument,
  useGetDefaultThemeQuery,
} from "../../services/settings/setting.rtk.api";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

const ThemeSettings: React.FC = () => {
  const { theme } = useTheme();

  // THEME CLASSES    const [changeThemeStatus,{isLoding:isChangingStatus}] = useChangeThemeStatusMutation();
  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
  const heading = theme === "dark" ? "text-white" : "text-gray-900";
  const crumbLink = theme === "dark" ? "text-gray-400" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

  const navigate = useNavigate();

  const { data: defaultTheme, isLoading } = useGetDefaultThemeQuery();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const hasCreatedForOrg = useRef<Record<string, boolean>>({});
  const iframeRef = useRef<ScaledIframeRef>(null);

  const cardClass =
    theme === "dark"
      ? "bg-[#1A1A1A] border border-[#2A2A2A] text-gray-300"
      : "bg-white border border-gray-200 text-gray-800";

  const cardHeader =
    theme === "dark"
      ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between bg-[#222222]"
      : "px-6 py-4 border-b border-gray-200 text-gray-900 flex items-center justify-between bg-gray-50";

  const tableHeaderClass =
    theme === "dark"
      ? "text-gray-200 font-semibold"
      : "text-gray-700 font-semibold";

  const tableRowClass =
    theme === "dark"
      ? "border-b border-[#2A2A2A] hover:bg-[#222222]"
      : "border-b border-gray-200 hover:bg-gray-50";

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Added useState for the themes so the toggle works
  const [themes, setThemes] = useState([
    {
      id: 1,
      name: "Default Theme",
      createdOn: "01/04/2026",
      updatedOn: "01/04/2026",
      mode: "Dark\nLight",
      status: "Inactive",
    },
    {
      id: 2,
      name: "Power Gym",
      createdOn: "15/04/2026",
      updatedOn: "25/04/2026",
      mode: "Dark",
      status: "Active",
    },
    {
      id: 3,
      name: "Sample Theme",
      createdOn: "02/04/2026",
      updatedOn: "13/04/2026",
      mode: "Dark\nLight",
      status: "Inactive",
    },
  ]);

  // Added the toggle function

  const toggleThemeStatus = async (id: string) => {
    // 1. Find the current item in the list to determine the next status
  };

  const handleEditThemeClick = (themeId: string) => {
    navigate(`/theme-settings/${themeId}`);
  };

  const handleCreateThemeClick = () => {
    navigate("/theme-settings/create");
  };

  const handleDeleteTheme = async () => {};
  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-GB"); // Format: DD/MM/YYYY
  };
  const handlePreviewThemeClick = (themeData: Theme | ThemeDocument) => {
    try {
      const assetBaseUrl =
        import.meta.env.VITE_APP_ASSET_URL ||
        "https://dev.assets.gymstream.app";

      // Resolve a logo field (object with key, string ID, or null) into a full URL
      const resolveLogoUrl = (logo: any): string | null => {
        if (!logo) return null;
        if (typeof logo === "object" && logo.key) {
          return `${assetBaseUrl}/files/app/themes/${logo.key}`;
        }
        // If it's already a string (an ID), return as-is (iframe can't render an ID, but it's the fallback)
        return typeof logo === "string" ? logo : null;
      };

      // Resolve a banner field the same way
      const resolveBannerUrl = (banner: any): string | null => {
        if (!banner) return null;
        if (typeof banner === "object" && banner.key) {
          return `${assetBaseUrl}/files/app/themes/${banner.key}`;
        }
        return typeof banner === "string" ? banner : null;
      };

      // Build a theme mode object with resolved URLs
      const buildPreviewTheme = (themeMode: any) => {
        if (!themeMode) return null;
        return {
          ...themeMode,
          branding: {
            ...themeMode.branding,
            logo: resolveLogoUrl(themeMode.branding?.logo),
          },
          assets: {
            ...themeMode.assets,
            banners: {
              device: resolveBannerUrl(themeMode.assets?.banners?.device),
              user: resolveBannerUrl(themeMode.assets?.banners?.user),
            },
          },
        };
      };

      const livePayload = {
        type: "CUSTOM",
        mode: themeData.mode || "DARK",
        defaultMode: themeData.defaultMode || "DARK",
        lightTheme: buildPreviewTheme(themeData.lightTheme),
        darkTheme: buildPreviewTheme(themeData.darkTheme),
      };
      const encodedTheme = btoa(
        unescape(encodeURIComponent(JSON.stringify(livePayload))),
      );
      const url = `${import.meta.env.VITE_TV_APP_URL}/preview-theme?theme=${encodedTheme}`;
      setPreviewUrl(url);
      setShowPreviewModal(true);
    } catch (e) {
      console.error("Failed to build preview URL", e);
      toast.error("Failed to open preview.");
    }
  };

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
        transition: "margin-left 300ms ease-in-out, width 300ms ease-in-out",
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-6">
        <h1 className={`text-2xl font-semibold ${heading} mb-2`}>Settings</h1>
        <div className="text-sm flex items-center gap-2">
          {/* Home Placeholder SVG */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-400"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <Link
            to="/"
            className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
          >
            Dashboard
          </Link>
          <span className={crumbSep}>&gt;</span>
          <span className="text-[#B6CB24] font-normal">Settings</span>
        </div>
      </div>

      {/* Main Table Card */}
      <div
        className={`${cardClass} rounded-xl overflow-hidden shadow-md w-full`}
      >
        <div className={cardHeader}>
          <span className="text-lg font-semibold">Default theme</span>
        </div>

        <div className="overflow-auto max-h-[calc(90vh-250px)]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center">
              <div className="w-12 h-12 border-4  border-[#2A2A2A] border-t-[#C6D92C] rounded-full animate-spin mb-4"></div>
              <p className="text-gray-400 font-medium">Loading Themes...</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead
                className={theme === "dark" ? "bg-[#2A2A2A]" : "bg-gray-100"}
              >
                <tr>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>
                    Theme Name
                  </th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>
                    Created On
                  </th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>
                    Updated On
                  </th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>Mode</th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>Status</th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>Action</th>
                </tr>
              </thead>
              <tbody>
                {defaultTheme ? (
                  <tr className={tableRowClass}>
                    <td className="px-6 py-4 font-medium">
                      {defaultTheme.name || "Default Theme"}
                    </td>
                    <td className="px-6 py-4">
                      {formatDate(defaultTheme.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      {formatDate(defaultTheme.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-pre-line leading-snug">
                      {defaultTheme.mode || "Dark\nLight"}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded text-xs ${defaultTheme.status === "Active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}
                      >
                        {defaultTheme.status || "Active"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handlePreviewThemeClick(defaultTheme)}
                        >
                          <svg
                            width="25"
                            height="21"
                            viewBox="0 0 25 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M22.7629 0H1.45295C0.653828 0 0 0.653828 0 1.45295V17.4354C0 18.2345 0.653828 18.8884 1.45295 18.8884H7.04682V20.0314H5.62292C5.35655 20.0314 5.13861 20.2493 5.13861 20.5157C5.13861 20.7821 5.35655 21 5.62292 21H7.53113H16.6847H18.5929C18.8593 21 19.0773 20.7821 19.0773 20.5157C19.0773 20.2493 18.8593 20.0314 18.5929 20.0314H17.169V18.8884H22.7629C23.562 18.8884 24.2159 18.2345 24.2159 17.4354V1.45295C24.2159 0.653828 23.562 0 22.7629 0ZM16.2004 20.0314H8.01545V18.8884H16.2004V20.0314ZM23.2472 17.4354C23.2472 17.7018 23.0293 17.9197 22.7629 17.9197H16.6847H7.53113H1.45295C1.18658 17.9197 0.968634 17.7018 0.968634 17.4354V1.45295C0.968634 1.18658 1.18658 0.968635 1.45295 0.968635H22.7629C23.0293 0.968635 23.2472 1.18658 23.2472 1.45295V17.4354Z"
                              fill="white"
                            />
                            <path
                              d="M12.1077 4.77551C8.96447 4.77551 6.02951 6.47062 4.45064 9.20217C4.36346 9.35231 4.36346 9.53635 4.45064 9.68649C6.03435 12.418 8.96932 14.1131 12.1077 14.1131C15.2461 14.1131 18.1859 12.418 19.7647 9.68649C19.8519 9.53635 19.8519 9.35231 19.7647 9.20217C18.1859 6.47062 15.2509 4.77551 12.1077 4.77551ZM12.1077 13.1445C9.4052 13.1445 6.87222 11.7352 5.4338 9.44433C6.87222 7.15351 9.4052 5.74415 12.1077 5.74415C14.8102 5.74415 17.3432 7.15351 18.7816 9.44433C17.3432 11.7352 14.8102 13.1445 12.1077 13.1445Z"
                              fill="#C6D92C"
                            />
                            <path
                              d="M12.1079 6.82349C10.6647 6.82349 9.48779 8.00038 9.48779 9.44364C9.48779 10.8869 10.6647 12.0638 12.1079 12.0638C13.5512 12.0638 14.7281 10.8869 14.7281 9.44364C14.7281 8.00038 13.5512 6.82349 12.1079 6.82349ZM12.1079 11.0952C11.1974 11.0952 10.4564 10.3542 10.4564 9.44364C10.4564 8.53313 11.1974 7.79212 12.1079 7.79212C13.0185 7.79212 13.7595 8.53313 13.7595 9.44364C13.7595 10.3542 13.0185 11.0952 12.1079 11.0952Z"
                              fill="white"
                            />
                          </svg>
                        </button>

                        <button
                          className=""
                          onClick={() => {
                            handleEditThemeClick(defaultTheme._id);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M16.4249 4.60509L17.4149 3.6151C18.2351 2.79497 19.5648 2.79497 20.3849 3.6151C21.205 4.43524 21.205 5.76493 20.3849 6.58507L19.3949 7.57506M16.4249 4.60509L9.76558 11.2644C9.25807 11.772 8.89804 12.4078 8.72397 13.1041L8 16L10.8959 15.276C11.5922 15.102 12.228 14.7419 12.7356 14.2344L19.3949 7.57506M16.4249 4.60509L19.3949 7.57506"
                              stroke="#C6D92C"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M18.9999 13.5C18.9999 16.7875 18.9999 18.4312 18.092 19.5376C17.9258 19.7401 17.7401 19.9258 17.5375 20.092C16.4312 21 14.7874 21 11.4999 21H11C7.22876 21 5.34316 21 4.17159 19.8284C3.00003 18.6569 3 16.7712 3 13V12.5C3 9.21252 3 7.56879 3.90794 6.46244C4.07417 6.2599 4.2599 6.07417 4.46244 5.90794C5.56879 5 7.21252 5 10.5 5"
                              stroke="#F3F3F6"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-gray-400"
                    >
                      No active default theme data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Live Preview Modal Overlay */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          {/* Modal Container */}
          <div className="bg-[#1A1A1A] rounded-xl shadow-2xl relative w-[70vw] h-[80vh] flex flex-col border border-gray-700">
            {/* Header / Close Button */}
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h3 className="text-white text-lg font-semibold">
                Theme Preview
              </h3>
              <button
                className="text-gray-400 hover:text-white transition-colors"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewUrl("");
                }}
              >
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Iframe Body */}
            <div className="flex-1 w-full h-full overflow-hidden rounded-b-xl bg-black">
              {previewUrl && <ScaledIframe src={previewUrl} ref={iframeRef} />}
            </div>

            <div className="px-3 py-2 flex items-center justify-center">
              <button
                className="flex items-center gap-2 px-6 py-2.5 border border-[#333] bg-[#222] hover:bg-[#2A2A2A] rounded-lg text-sm font-medium transition-colors text-white shadow-sm"
                onClick={() => iframeRef.current?.enterFullscreen()}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                Preview Full Screen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeSettings;
